import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTimesheetProvider } from "../../../../Providers/Consultant/Timesheet.js";
import moment from "moment";
import DatePicker from "../../../../Components/DatePicker.jsx";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { LinkIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { timesheetStatus } from "../../../../Utils/constants.js";
import { Button, Modal } from "antd";
import {
  Autocomplete,
  Checkbox,
  Chip,
  FormControlLabel,
  TextField,
  Tooltip,
  Select,
} from "@mui/material";
import isEmail from "validator/lib/isEmail";
import isInt from "validator/lib/isInt";
import dayjs from "dayjs";

const defaultTimeObject = { 0: "", 1: "", 2: "", 3: "", 4: "", 5: "", 6: "" };
export const NewTimesheet = () => {
  const [date, setDate] = useState({
    date: moment.utc().startOf("week"),
    fromApi: false,
  });

  const [dayDetails, setDayDetails] = useState([]);
  const [open, setOpen] = useState(false);
  const [time, setTime] = useState(defaultTimeObject);
  const [status, setStatus] = useState(timesheetStatus.none);
  const [comments, setComments] = useState(null);
  const [approvalDate, setApprovalDate] = useState(null);
  const [approvedHours, setApprovedHours] = useState(null);
  const [approvedMinutes, setApprovedMinutes] = useState(null);
  const [approvedBy, setApprovedBy] = useState(null);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [emails, setEmails] = useState([]);
  const [secure, setSecure] = useState(false);
  const [pin, setPin] = useState("");
  const [weeks, setWeeks] = useState("1");

  const handleChange = (event) => {
    setWeeks(event.target.value);
  };

  const {
    selectedJobDetails,
    selectedTimesheet,
    addNewTimesheet,
    timesheetMaster,
    timesheetDetails,
    timesheetOtherDetails,
    fetchTimesheetsByDate,
    updateTimesheet,
    generateSecureLink,
  } = useTimesheetProvider();

  useEffect(() => {
    if (selectedJobDetails?.job?.id && !selectedTimesheet) {
      fetchTimesheetsByDate({
        jobId: selectedJobDetails?.job?.id,
        startDate: moment.utc().startOf("week").toISOString(),
      });
    }
  }, []);

  useEffect(() => {
    if (selectedJobDetails?.job?.start_date) {
      const cur = moment.utc().startOf("week");
      const start = moment
        .utc(selectedJobDetails?.job?.start_date, "YYYY-MM-DD")
        .startOf("week");

      if (cur.isBefore(start)) setDate({ date: start, fromApi: false });
    }
  }, [selectedJobDetails?.job?.start_date]);

  useEffect(() => {
    const timesheetList = timesheetMaster?.[selectedJobDetails?.job?.id] ?? [];
    if (timesheetList.length > 0) {
      const res = timesheetList.find((item) => item?.id === selectedTimesheet);
      if (res) {
        setApprovalDate(res.updatedAt);
        setApprovedHours(res.hours);
        setApprovedMinutes(res.minutes);
        setDate({ date: moment.utc(res.start), fromApi: true });
        setStatus(res.status);
        setComments(res?.clientComments);
      }
    }

    if (timesheetOtherDetails[0]?.approvedBy)
      setApprovedBy(
        timesheetOtherDetails[0]?.approvedBy?.firstName +
        " " +
        timesheetOtherDetails[0]?.approvedBy?.lastName +
        " (" +
        timesheetOtherDetails[0]?.approvedBy?.email +
        ")"
      );
    else if (timesheetOtherDetails[0]?.approvedByExternal)
      setApprovedBy(timesheetOtherDetails[0]?.approvedByExternal);
  }, [
    timesheetMaster?.[selectedJobDetails?.job?.id],
    selectedTimesheet,
    timesheetOtherDetails,
    updateTimesheet
  ]);

  useEffect(() => {
    if (!date.fromApi && !selectedTimesheet) {
      const temp = [];
      for (let i = 0; i < 7; i++) {
        temp.push({
          date: moment.utc(date.date).startOf("week").add(i, "days").toISOString(), // prettier-ignore
          hours: 0,
          minutes: 0,
          description: "",
        });
      }

      setTime(defaultTimeObject);
      setStatus(timesheetStatus.none);
      setComments(null);
      setDayDetails(temp);
    }
  }, [date, selectedTimesheet]);

  useEffect(() => {
    if (timesheetDetails?.[selectedTimesheet]) {
      const cur = timesheetDetails?.[selectedTimesheet];
      const temp = { ...defaultTimeObject };
      cur?.forEach((day, index) => {
        const hours = day.hours.toString().padStart(2, "0");
        const minutes = day.minutes.toString().padStart(2, "0");

        temp[index] =
          hours === "00" && minutes === "00" ? "" : `${hours}:${minutes}`;
      });

      setTime(temp);
      setDayDetails(cur);
    }
  }, [timesheetDetails?.[selectedTimesheet]]);

  const validateDayDetails = () => {
    const isValid = dayDetails.every((item, index) => {
      if (time[index].length === 0) {
        return item.hours === 0 && item.minutes === 0;
      } else {
        if (item.hours > 24 || item.minutes > 60 || (item.hours == 24 && item.minutes > 0)) return false;

        return (
          item.hours === parseInt(time[index].slice(0, 2)) &&
          item.minutes === parseInt(time[index].slice(3))
        );
      }
    });

    return isValid;
  };

  const handleSave = () => {
    // Edit selected timesheet
    if (selectedTimesheet !== null) {
      if (status === timesheetStatus.saved) {
        const isValid = validateDayDetails();
        setStatus(timesheetStatus.none);
      } else {
        const isValid = validateDayDetails();
        if (isValid) {
          updateTimesheet({ dayData: dayDetails }).then(() =>
            setStatus(timesheetStatus.saved)
          );
        } else {
          Swal.fire({
            icon: "error",
            title: "Invalid Format",
            text: "Please enter the hours in the correct format",
          });
        }
      }
    }

    // Create new timesheet
    else {
      const isValid = validateDayDetails();
      if (isValid) {
        addNewTimesheet({
          start: moment.utc(date.date).startOf("week").toISOString(),
          end: moment.utc(date.date).endOf("week").toISOString(),
          dayDetails,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Invalid Format",
          text: "Please enter the hours in the correct format",
        });
      }
    }
  };

  const handleSubmit = () => {
    if (selectedTimesheet === null) {
      const isValid = validateDayDetails();
      if (isValid) {
        addNewTimesheet({
          start: moment.utc(date.date).startOf("week").toISOString(),
          end: moment.utc(date.date).endOf("week").toISOString(),
          dayDetails,
          submit: true,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Invalid Format",
          text: "Please enter the hours in the correct format",
        });
      }
    } else {
      const isValid = validateDayDetails();
      if (isValid) {
        updateTimesheet({ dayData: dayDetails, submit: true }).then(() =>
          setStatus(timesheetStatus.submitted)
        );
      } else {
        Swal.fire({
          icon: "error",
          title: "Invalid Format",
          text: "Please enter the hours in the correct format",
        });
      }
    }
  };

  const submitTimesheetViaLink = ({ shouldCopy = false }) => {
    if (
      (shouldCopy === false && emails.length === 0) ||
      emails.some((email) => !isEmail(email))
    ) {
      Swal.fire({
        icon: "error",
        title: "Invalid E-Mail",
        text: "Please enter a valid E-Mail address",
      });
      return;
    }

    if (secure) {
      if (!isInt(pin, { min: 0, max: 9999 })) {
        Swal.fire({
          icon: "error",
          title: "Invalid PIN",
          text: "Please enter a valid PIN",
        });
        return;
      }
    }

    generateSecureLink({
      timesheetId: selectedTimesheet,
      emails,
      pin,
      numberOfWeeks: parseInt(weeks),
    }).then((linkId) => {
      if (linkId !== null) {

        const baseUrl = process.env.REACT_APP_BASE_URL === "https://dev.api.prolegion.com" ? "https://dev.prolegion.com" : process.env.REACT_APP_BASE_URL === "https://demo.api.prolegion.com" ? "https://demo.prolegion.com" : "https://prolegion.com"

        if (shouldCopy) {
          navigator.clipboard
            .writeText(`${baseUrl}/timesheet/${linkId}`)
            .then(() =>
              Swal.fire({
                icon: "success",
                title: "Success",
                text: "Timesheet Link Copied to Clipboard",
              })
            );
        } else {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Timesheet submitted successfully",
          });
        }

        setModalOpen(false);
        setEmails([]);
        setSecure(false);
        setPin("");
      }
    });
  };

  return (
    <div className="pb-8">
      <div className="flex justify-between mb-4 mt-8">
        {/* Date Input */}
        <div className="relative">
          <div className="flex items-center select-none">
            {/* Left Arrow */}
            <ChevronLeft
              sx={{ fontSize: 28 }}
              className="border-[1px] rounded-md mr-2 border-black cursor-pointer"
              onClick={() => {
                const newDate = moment.utc(date.date).subtract(1, "week");
                if (
                  newDate >=
                  moment
                    .utc(selectedJobDetails?.job?.start_date)
                    .startOf("week")
                ) {
                  setComments(null);
                  setApprovalDate(null);
                  setApprovedHours(null);
                  setApprovedMinutes(null)
                  setDate({ date: newDate, fromApi: false });
                  setStatus(timesheetStatus.none);

                  if (selectedJobDetails?.job?.id) {
                    fetchTimesheetsByDate({
                      jobId: selectedJobDetails?.job?.id,
                      startDate: newDate.startOf("week").toISOString(),
                    });
                  }
                }
              }}
            />

            {/* Display Selected Week */}
            <input
              readOnly
              onClick={() => setOpen(!open)}
              className="h-10 w-[350px] text-white bg-[#2EBAA3] p-1 rounded-md border-0 cursor-pointer text-center outline-0"
              value={
                moment.utc(date.date).startOf("week").format("Do MMM, YYYY") +
                " - " +
                moment.utc(date.date).endOf("week").format("Do MMM, YYYY")
              }
            />

            {/* Right Arrow */}
            <ChevronRight
              sx={{ fontSize: 28 }}
              className="border-[1px] rounded-md ml-2 border-black cursor-pointer"
              onClick={() => {
                const newDate = moment.utc(date.date).add(1, "week");
                setDate({ date: newDate, fromApi: false });
                setStatus(timesheetStatus.none);
                setComments(null);
                setApprovalDate(null);
                setApprovedHours(null);
                setApprovedMinutes(null)
                if (selectedJobDetails?.job?.id) {
                  fetchTimesheetsByDate({
                    jobId: selectedJobDetails?.job?.id,
                    startDate: newDate.startOf("week").toISOString(),
                  });
                }
              }}
            />
          </div>

          {/* Date Picker */}
          <DatePicker
            className="absolute invisible bottom-0 left-[71px]"
            open={open}
            disabled={selectedTimesheet !== null}
            placeholder="Select Week"
            value={date.date === null ? null : moment.utc(date.date)}
            onSelect={(_) => setOpen(false)}
            onBlur={() => setOpen(false)}
            onChange={(date) => {
              setOpen(false);
              setDate({
                date: moment.utc(date.format("YYYY-MM-DD")),
                fromApi: false,
              });
            }}
            disabledDate={(current) =>
              selectedJobDetails?.job?.start_date
                ? moment.utc(current.unix() * 1000) <
                moment.utc(selectedJobDetails.job.start_date)
                : false
            }
            picker="week"
          />
        </div>

        <div className="flex gap-x-4">
          {status === timesheetStatus.submitted ||
            status === timesheetStatus.approved ? null : (
            <div className="">
              <div className="flex items-center justify-end gap-x-4">
                <button
                  style={{
                    backgroundColor: date.date > new Date() ? "gray" : "#139588",
                  }}
                  className="mb-2 w-[109px] h-[42px] bg-[#139588] rounded-[8px] text-white"
                  onClick={(e) => {
                    e.preventDefault();
                    if (date.date > new Date()) {
                      Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: "You cannot save the timesheet for next week",
                      });
                    } else if (status === timesheetStatus.rejected) {
                      setStatus(timesheetStatus.none);
                    } else {
                      handleSave();
                    }
                  }}
                >
                  {status === timesheetStatus.saved
                    ? "Edit"
                    : status === timesheetStatus.rejected
                      ? "Update"
                      : "Save"}
                </button>

                <button
                  style={{
                    backgroundColor: date.date > new Date() ? "gray" : "#139588",
                  }}
                  className="mb-2 w-[109px] h-[42px] bg-[#139588] rounded-[8px] text-white"
                  onClick={(e) => {
                    e.preventDefault();
                    if (date.date > new Date()) {
                      Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: "You cannot submit the timesheet for next week",
                      });
                    } else {
                      const timesheetList = timesheetMaster?.[selectedJobDetails?.job?.id]; // prettier-ignore
                      const originalStatus = timesheetList?.find((item) => item?.id === selectedTimesheet)?.status; // prettier-ignore

                      if (originalStatus === timesheetStatus.rejected) {
                        if (status === timesheetStatus.rejected) {
                          Swal.fire({
                            icon: "error",
                            title: "Error",
                            text: "Please update the timesheet before resubmitting",
                          });
                        } else {
                          handleSubmit();
                        }
                      } else if (
                        originalStatus === timesheetStatus.saved ||
                        !originalStatus
                      ) {
                        handleSubmit();
                      }
                    }
                  }}
                >
                  {status === timesheetStatus.rejected ? "Resubmit" : "Submit"}
                </button>
              </div>
            </div>
          )}
          {/* Submit via Link Button */}
          {status === timesheetStatus.saved || status === timesheetStatus.submitted ? (
            <button
              className="px-2 h-[42px] py-0.5 border border-[#139588] rounded-[8px] text-[#139588] flex items-center gap-x-1"
              onClick={() => setModalOpen(true)}
            >
              <LinkIcon className="w-5 h-5" /> Submit via Link
            </button>
          ) : null}

          {/* Status Badge */}
          {status === timesheetStatus.submitted ||
            status === timesheetStatus.approved ||
            status === timesheetStatus.rejected ? (
            <div className="flex flex-col">
              <div
                className="px-2 py-2 min-w-min rounded-[5px] flex justify-center font-[18px] leading-[22px]"
                style={
                  status === timesheetStatus.submitted
                    ? { background: "#FFAD334D", color: "#FFAD33" }
                    : status === timesheetStatus.approved
                      ? { background: "#2EBAA34D", color: "#2EBAA3" }
                      : { background: "#FF33334D", color: "#FF3333" }
                }
              >
                {status[0] + status.slice(1).toLowerCase()}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex justify-end -mt-4 mb-4">
        {(status === timesheetStatus.approved ||
          status === timesheetStatus.rejected) && (
            <div className="flex flex-row items-center">
              <h1
                // style={{ width: "160px" }}
                className=""
              >
                {status === timesheetStatus.approved
                  ? "Approved On : " +
                  dayjs(approvalDate).locale("en").format("DD MMMM YYYY h:mm A")
                  : "Rejected On : " +
                  dayjs(approvalDate)
                    .locale("en")
                    .format("DD MMMM YYYY h:mm A")}
              </h1>
              {/* <span className="w-full text-sm flex justify-end">{dayjs(approvalDate).locale('en').format('DD MMMM YYYY h:mm A')}</span> */}
            </div>
          )}
      </div>

      {comments || approvalDate || approvedHours || approvedMinutes ? (
        <div className="flex md:flex-row justify-between flex-col bg-white p-4 mb-4 rounded-lg">
          {comments ? (
            <div className="flex flex-col items-center">
              {(status === timesheetStatus.approved ||
                status === timesheetStatus.rejected) && (
                  <div>
                    <h1
                      style={{ width: "300px" }}
                      className={
                        status === timesheetStatus.approved
                          ? "text-[#139588] font-medium"
                          : "text-red-500 font-medium"
                      }
                    >
                      {(status === timesheetStatus.approved &&
                        "Approved By : ") ||
                        (status === timesheetStatus.rejected && "Rejected By : ")}
                    </h1>
                    <span className="w-full text-sm">{approvedBy}</span>
                  </div>
                )}
              <div>
                <h1
                  style={{ width: "300px" }}
                  className={
                    status === timesheetStatus.approved
                      ? "text-[#139588] font-medium"
                      : "text-red-500 font-medium"
                  }
                >
                  Message
                </h1>
                <span className="w-full text-sm">
                  {comments?.length > 0 ? comments : "None"}
                </span>
              </div>
            </div>
          ) : null}
          {timesheetStatus.approved ? (
            <div className="flex flex-row">
              <h1
                style={{ width: "200px" }}
                className={"text-[#139588] font-medium"}
              >
                Total Hours :{" "}
              </h1>
              <span className="w-full text-sm">
                {(approvedHours || approvedMinutes) &&
                  approvedMinutes === 0
                  ? approvedHours + ":00"
                  : approvedHours + ":" + approvedMinutes}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="left">Hours</TableCell>
              <TableCell align="left">Description</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {dayDetails.map((day, index) => {
              const inputDisabled = [
                timesheetStatus.submitted,
                timesheetStatus.approved,
                timesheetStatus.rejected,
                timesheetStatus.saved,
              ].includes(status);

              return (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {moment.utc(day.date).format("dddd, D MMMM")}
                  </TableCell>
                  <TableCell align="left">
                    {inputDisabled ? (
                      <input
                        className="w-[120px]"
                        disabled
                        value={
                          day.hours.toString().padStart(2, "0") +
                          " : " +
                          day.minutes.toString().padStart(2, "0")
                        }
                      />
                    ) : (
                      <input
                        disabled={inputDisabled}
                        className="flex-1 w-[120px]"
                        maxLength={5}
                        placeholder="HH:MM"
                        style={
                          parseInt(time[index].split(":")[0]) >= 24
                            ? {
                              color: "#FF3131",
                              borderWidth: "1px",
                            }
                            : null
                        }
                        value={time[index]}
                        onBlur={({ target: { value } }) => {
                          if (value.length === 0) {
                            const temp = [...dayDetails];
                            temp[index].hours = 0;
                            temp[index].minutes = 0;
                            setDayDetails(temp);
                            setTime({ ...time, [index]: "" });
                          } else if (isInt(value)) {
                            if (parseInt(value) <= 0) {
                              setTime({ ...time, [index]: "" });
                            } else {
                              const temp = [...dayDetails];
                              const hours = value.padStart(2, "0");
                              temp[index].hours = parseInt(value);
                              temp[index].minutes = 0;
                              setDayDetails(temp);
                              setTime({ ...time, [index]: `${hours}:00` });
                            }
                          }
                        }}
                        onChange={({ target: { value } }) => {
                          if (value.length === 0)
                            setTime({ ...time, [index]: "" });
                          else if (value.length < 3) {
                            if (isInt(value))
                              setTime({ ...time, [index]: value });
                          } else if (value.length === 3) {
                            if (value[2] && isInt(value.slice(0, 1)))
                              setTime({ ...time, [index]: value.slice(0, 2) + ":" });
                            else
                              setTime({ ...time, [index]: ":" + value.slice(1) })
                          } else {
                            if (isInt(value.slice(3), { min: 0, max: 59 })) {
                              if (value.length === 5) {
                                const temp = [...dayDetails];
                                temp[index].hours = parseInt(value.slice(0, 2));
                                temp[index].minutes = parseInt(value.slice(3));
                                setDayDetails(temp);
                              }

                              setTime({ ...time, [index]: value });
                            }
                          }
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="left">
                    <input
                      disabled={inputDisabled}
                      className="flex-1 w-[90%]"
                      value={day.description}
                      onChange={(e) => {
                        const temp = [...dayDetails];
                        temp[index].description = e.target.value;
                        setDayDetails(temp);
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        open={modalOpen}
        title="Submit Timesheet via Link"
        onCancel={() => {
          setModalOpen(false);
          setEmails([]);
          setSecure(false);
          setPin("");
          setWeeks("1");
        }}
        footer={
          <div className="flex flex-row justify-between">
            <Button
              onClick={() => submitTimesheetViaLink({ shouldCopy: true })}
              style={{
                border: "1px solid #787878",
                borderRadius: "8px",
                color: "#787878",
                width: "177px",
              }}
            >
              Copy Link
            </Button>
            <Button
              type="primary"
              style={{
                background: "#2EBAA3",
                borderRadius: "8px",
                width: "177px",
              }}
              onClick={() => submitTimesheetViaLink({ shouldCopy: false })}
            >
              Submit
            </Button>
          </div>
        }
      >
        <div className="flex items-center gap-x-6 mb-3 pt-2">
          <span className="w-[50px]">Weeks</span>
          <FormControl size="small" fullWidth>
            <Select
              value={weeks}
              onChange={handleChange}
              placeholder="Select number of weeks"
              sx={{
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "1px",
                  borderColor: "rgba(0,0,0,0.87)",
                },
              }}
            >
              <MenuItem value={1}>
                Current week (
                {moment.utc(date.date).startOf("week").format("DD MMM to ")}
                {moment.utc(date.date).endOf("week").format("DD MMM")})
              </MenuItem>

              {/* For multiple weeks */}
              {[2, 3, 4].map((value) => (
                <MenuItem key={value} value={value}>
                  Last {value} weeks (
                  {moment
                    .utc(date.date)
                    .subtract(value - 1, "week")
                    .format("DD MMM to ")}
                  {moment.utc(date.date).endOf("week").format("DD MMM")})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <Autocomplete
          multiple
          freeSolo
          clearIcon={null}
          clearOnBlur
          value={emails}
          options={[]}
          onChange={(e, value) =>
            setEmails(value.filter((email) => isEmail(email)))
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option} {...getTagProps({ index })} />
            ))
          }
          renderInput={(params) => (
            <div className="flex flex-row items-center gap-x-6">
              <span className="w-[50px]">Email</span>
              <Tooltip
                arrow
                title="To add multiple addresses, press enter after each address"
              >
                <TextField
                  {...params}
                  size="small"
                  required
                  label="E-Mail"
                  onBlur={(e) => {
                    if (isEmail(e.target.value)) {
                      if (!emails.includes(e.target.value))
                        setEmails([...emails, e.target.value]);
                    }
                  }}
                  sx={{
                    "& label.Mui-focused": {
                      color: "rgba(0,0,0,0.6)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "rgba(0,0,0,0.87)",
                        borderWidth: "1px",
                      },
                    },
                  }}
                />
              </Tooltip>
            </div>
          )}
        />

        <div className="pl-2">
          <FormControlLabel
            control={
              <Checkbox
                checked={secure}
                onChange={(_, val) => setSecure(val)}
                sx={{ paddingLeft: 0, paddingRight: "0.25rem" }}
                disableRipple
              />
            }
            label="Add Pin (Communicate the pin to client)"
            className="mt-4"
          />
        </div>

        {secure ? (
          <div className="flex space-x-10 items-center">
            <span>PIN</span>
            <TextField
              size="small"
              label="PIN"
              placeholder="Enter a 4-digit PIN"
              sx={{
                marginTop: "0.25rem",
                marginBottom: "0.5rem",
                "& label.Mui-focused": {
                  color: "rgba(0,0,0,0.6)",
                },
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "rgba(0,0,0,0.87)",
                    borderWidth: "1px",
                  },
                },
              }}
              maxLength="4"
              value={pin}
              fullWidth
              onChange={(e) => {
                const str = e.target.value;
                if (str.length === 0) setPin("");
                else if (isInt(str, { min: 0, max: 9999 })) setPin(str);
              }}
            />
          </div>
        ) : null}
      </Modal>
    </div>
  );
};
